import {
  getFirstFocusable,
  getFocusableElements,
  getLastFocusable,
  safelyFocus,
} from './focusable-elements';
import type {
  ContainerRef,
  FocusChangeListener,
  FocusScopeOptions,
  FocusScopeState,
  FocusScopeType,
  ResolvedFocusScopeOptions,
} from './types';

// ─── Option Resolution ────────────────────────────────────────────────────────

/**
 * Default options per scope type.
 * Dialogs trap focus and auto-focus their first element.
 * Other scopes restore focus but do not trap or auto-focus.
 */
const TYPE_DEFAULTS: Record<FocusScopeType, Omit<ResolvedFocusScopeOptions, 'returnFocusRef'>> = {
  global: {
    type: 'global',
    trapFocus: false,
    restoreFocus: false,
    autoFocusFirst: false,
    initialFocusSelector: '',
  },
  page: {
    type: 'page',
    trapFocus: false,
    restoreFocus: true,
    autoFocusFirst: false,
    initialFocusSelector: '',
  },
  form: {
    type: 'form',
    trapFocus: false,
    restoreFocus: true,
    autoFocusFirst: false,
    initialFocusSelector: '',
  },
  table: {
    type: 'table',
    trapFocus: false,
    restoreFocus: true,
    autoFocusFirst: false,
    initialFocusSelector: '',
  },
  dialog: {
    type: 'dialog',
    trapFocus: true,
    restoreFocus: true,
    autoFocusFirst: true,
    initialFocusSelector: '',
  },
  tooltip: {
    type: 'tooltip',
    trapFocus: false,
    restoreFocus: true,
    autoFocusFirst: false,
    initialFocusSelector: '',
  },
};

function resolveOptions(options: FocusScopeOptions): ResolvedFocusScopeOptions {
  const type = options.type ?? 'form';
  const base = TYPE_DEFAULTS[type];
  return {
    type,
    trapFocus: options.trapFocus ?? base.trapFocus,
    restoreFocus: options.restoreFocus ?? base.restoreFocus,
    autoFocusFirst: options.autoFocusFirst ?? base.autoFocusFirst,
    initialFocusSelector: options.initialFocusSelector ?? base.initialFocusSelector,
    returnFocusRef: options.returnFocusRef,
  };
}

// ─── FocusRegistry ────────────────────────────────────────────────────────────

/**
 * Framework-agnostic focus scope registry.
 *
 * Responsibilities:
 *  - Maintain an ordered stack of active focus scopes (LIFO).
 *  - Record which element had focus before each scope activated, so it can
 *    be restored when the scope deactivates (e.g. dialog closes).
 *  - Provide imperative focus utilities (focusFirst, focusLast, focusById).
 *  - Notify React subscribers on any state change (for useSyncExternalStore).
 *
 * React integration is in FocusManager.tsx and use-focus.ts.
 * This class has zero React imports and is testable in isolation.
 *
 * Scope stack mental model:
 *   [page] → [form] → [dialog] → [nested-dialog]
 *   Topmost scope is the one currently receiving keyboard focus/interaction.
 *   Deactivating a scope pops it and restores focus to the element recorded
 *   when that scope was pushed.
 */
export class FocusRegistry {
  private readonly _scopes = new Map<string, FocusScopeState>();
  /** Ordered activation stack — index 0 is oldest, last index is newest/topmost. */
  private readonly _stack = new Array<string>();
  private readonly _listeners = new Set<FocusChangeListener>();

  // ─── Registration ──────────────────────────────────────────────────────────

  /**
   * Register a scope. Does NOT activate it.
   *
   * If a scope with the same `id` is already registered, its options are
   * updated (container ref and options replace the previous values).
   */
  registerScope(id: string, containerRef: ContainerRef, options: FocusScopeOptions = {}): void {
    const existing = this._scopes.get(id);

    if (existing) {
      // Update container and options — preserve activation state
      (existing as { containerRef: ContainerRef }).containerRef = containerRef;
      (existing as { options: ResolvedFocusScopeOptions }).options = resolveOptions(options);
      return; // No notify — no visible change
    }

    const state: FocusScopeState = {
      id,
      containerRef,
      options: resolveOptions(options),
      previousFocus: null,
      activatedAt: 0,
    };

    this._scopes.set(id, state);
    // No notify — registering alone doesn't change focus behavior
  }

  /**
   * Deactivate (if active) and remove a scope entirely.
   * Called on component unmount.
   */
  unregisterScope(id: string): void {
    this.deactivateScope(id); // Pops from stack and restores focus if needed
    this._scopes.delete(id);
    this._notify();
  }

  // ─── Activation ────────────────────────────────────────────────────────────

  /**
   * Activate a scope:
   * 1. Record the currently focused element as `previousFocus`.
   * 2. Push this scope to the top of the activation stack.
   * 3. If `options.autoFocusFirst` is true, focus the first focusable element
   *    (deferred to next animation frame to allow CSS transitions to complete).
   *
   * If the scope is already active (in the stack), it is moved to the top.
   */
  activateScope(id: string): void {
    const scope = this._scopes.get(id);

    if (!scope) {
      console.warn(`[FocusRegistry] Cannot activate unregistered scope "${id}".`);
      return;
    }

    // Remove from stack if already present (re-activation moves to top)
    const existingIdx = this._stack.indexOf(id);
    if (existingIdx !== -1) {
      this._stack.splice(existingIdx, 1);
    }

    // Capture current focus before we take it over
    scope.previousFocus = document.activeElement;
    scope.activatedAt = Date.now();

    // Push to top
    this._stack.push(id);

    // Auto-focus deferred to next frame so CSS entry animations can settle
    if (scope.options.autoFocusFirst) {
      const container = scope.containerRef.current;
      if (container) {
        const selector = scope.options.initialFocusSelector || undefined;
        requestAnimationFrame(() => {
          // Guard: scope may have been deactivated during the frame delay
          if (!this.isScopeActive(id)) return;
          const target = getFirstFocusable(container, selector);
          safelyFocus(target, { preventScroll: false });
        });
      }
    }

    this._notify();
  }

  /**
   * Deactivate a scope:
   * 1. Pop it from the activation stack.
   * 2. If `options.restoreFocus` is true, restore focus to the element recorded
   *    at activation time — or to `options.returnFocusRef.current` if set.
   *
   * No-op if the scope is not currently active.
   */
  deactivateScope(id: string): void {
    const stackIdx = this._stack.indexOf(id);
    if (stackIdx === -1) return; // Not active — nothing to do

    const scope = this._scopes.get(id);
    this._stack.splice(stackIdx, 1);

    if (scope?.options.restoreFocus) {
      // Explicit return target takes priority over recorded previous focus
      const returnTarget = scope.options.returnFocusRef?.current ?? null;
      const restoreTarget = returnTarget ?? scope.previousFocus;
      this._safeRestoreFocus(restoreTarget);
    }

    this._notify();
  }

  /**
   * Update just the `returnFocusRef` on an already-registered scope.
   * Useful when the trigger element changes after registration.
   */
  setReturnFocusRef(id: string, ref: ContainerRef | undefined): void {
    const scope = this._scopes.get(id);
    if (!scope) return;
    // ResolvedFocusScopeOptions has returnFocusRef as mutable
    (scope.options as { returnFocusRef: ContainerRef | undefined }).returnFocusRef = ref;
  }

  // ─── Queries ───────────────────────────────────────────────────────────────

  isScopeActive(id: string): boolean {
    return this._stack.includes(id);
  }

  /** Returns the scope ID at the top of the stack (most recently activated). */
  getActiveScopeId(): string | null {
    return this._stack[this._stack.length - 1] ?? null;
  }

  /** Returns the full state of the topmost scope, or null if stack is empty. */
  getActiveScope(): Readonly<FocusScopeState> | null {
    const id = this.getActiveScopeId();
    return id ? (this._scopes.get(id) ?? null) : null;
  }

  getScope(id: string): Readonly<FocusScopeState> | undefined {
    return this._scopes.get(id);
  }

  getStackIds(): readonly string[] {
    return this._stack;
  }

  getStackDepth(): number {
    return this._stack.length;
  }

  // ─── Focus Utilities ───────────────────────────────────────────────────────

  /**
   * Focus the first focusable element inside `container`.
   * Respects `[data-autofocus]`, `[autofocus]`, and the provided selector.
   * Returns the element that received focus, or null.
   */
  focusFirst(
    container: HTMLElement,
    selector?: string,
    options?: FocusOptions,
  ): HTMLElement | null {
    const el = getFirstFocusable(container, selector);
    if (el) safelyFocus(el, options);
    return el;
  }

  /** Focus the last focusable element inside `container`. */
  focusLast(container: HTMLElement, options?: FocusOptions): HTMLElement | null {
    const el = getLastFocusable(container);
    if (el) safelyFocus(el, options);
    return el;
  }

  /** Focus any element directly. */
  focusElement(el: HTMLElement, options?: FocusOptions): void {
    safelyFocus(el, options);
  }

  /**
   * Focus an element within `container` by its DOM `id` attribute.
   * Falls back to searching the full document if not found in container.
   */
  focusById(id: string, container?: HTMLElement): HTMLElement | null {
    const root = container ?? document;
    const query = `#${CSS.escape(id)}`;
    const el = root.querySelector<HTMLElement>(query);
    if (el) {
      safelyFocus(el);
      return el;
    }
    return null;
  }

  /**
   * Get all focusable elements within a container.
   * Delegates to `getFocusableElements` for external use.
   */
  getFocusableElements(container: HTMLElement): HTMLElement[] {
    return getFocusableElements(container);
  }

  // ─── Subscriptions ─────────────────────────────────────────────────────────

  /**
   * Subscribe to any focus scope state change.
   * Returns an unsubscribe function.
   * Used by React's `useSyncExternalStore` in consumer hooks.
   */
  subscribe(listener: FocusChangeListener): () => void {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }

  // ─── Debug ─────────────────────────────────────────────────────────────────

  debug(): void {
    console.warn('[FocusRegistry] scopes:', [...this._scopes.values()]);
    console.warn('[FocusRegistry] stack:', [...this._stack]);
    console.warn('[FocusRegistry] active:', this.getActiveScopeId());
  }

  // ─── Private ───────────────────────────────────────────────────────────────

  private _safeRestoreFocus(target: Element | null): void {
    if (!target) return;
    if (!(target instanceof HTMLElement)) return;
    if (!document.contains(target)) return;

    // Use preventScroll so restoring focus on dialog close doesn't jump the page
    safelyFocus(target, { preventScroll: true });
  }

  private _notify(): void {
    for (const listener of this._listeners) {
      listener();
    }
  }
}
